<template>
  <div class="entity-form">
    <v-container fluid id="fcreate">
      <breadcrumbs/>
      <h1 class="mb-2">
        {{ title }}
      </h1>

      <v-form v-model="valid" ref="form">
        <v-row>
          <v-col cols="12">
            <v-card>
              <v-card-text>
                <v-text-field
                  label="Название"
                  v-model="priceList.name"
                  :rules="rules.required"
                  :loading="loading"
                  :disabled="loading"
                  outlined
                ></v-text-field>
                <item-table
                  :items="filteredPriceListItemsForTable"
                  v-model="priceList.items"
                  :loading="loading"
                  @input="handlePriceListItemsInput"
                  @only-active-changed="preparePriceListItems"
                  @only-with-price-changed="setShowOnlyWithPriceItems"
                >
                </item-table>
              </v-card-text>

              <v-card-text>
                <v-row v-if="getId">
                  <v-col cols="4">
                    <v-btn
                      class="mb-2"
                      block
                      large
                      color="primary"
                      :loading="saving"
                      :disabled="loading"
                      @click.prevent="editEntity({redirect: true})"
                    >
                      Сохранить
                    </v-btn>
                  </v-col>
                  <v-col cols="4">
                    <v-btn
                      class="mb-2"
                      block
                      large
                      color="success"
                      :loading="saving"
                      :disabled="loading"
                      @click.prevent="editEntity({redirect: false})"
                    >
                      Применить
                    </v-btn>
                  </v-col>
                  <v-col cols="4">
                    <v-btn
                      block
                      large
                      color="error"
                      :loading="saving"
                      :disabled="loading"
                      @click.prevent="goToListing"
                    >
                      Отменить
                    </v-btn>
                  </v-col>
                </v-row>
                <v-row v-else>
                  <v-col>
                    <v-btn
                      block
                      class="mb-2"
                      large
                      color="primary"
                      :loading="saving"
                      :disabled="loading"
                      @click="createNewEntity"
                    >
                      Создать
                    </v-btn>
                  </v-col>
                  <v-col>
                    <v-btn
                      block
                      large
                      color="error"
                      :loading="saving"
                      :disabled="loading"
                      @click.prevent="$router.back()"
                    >
                      Отмена
                    </v-btn>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-form>

      <v-snackbar v-model="snackbar">
        Исправьте ошибки и повторите попытку
        <template v-slot:action="{ attrs }">
          <v-btn
            color="red"
            text
            v-bind="attrs"
            @click="snackbar = false"
          >
            Закрыть
          </v-btn>
        </template>
      </v-snackbar>
    </v-container>
  </div>
</template>

<script>
  import rules from '@/mixins/rules'
  import snackbarMessage from '@/mixins/snackbar-message'
  import PriceListDataService from '@/services/api/PriceListDataService'
  import ItemTable from '@/components/price-list/ItemTable.vue'
  import { mapGetters } from 'vuex'
  import { isNumber } from 'lodash'

  export default {
    name: 'PriceListForm',
    components: { ItemTable },
    mixins: [rules, snackbarMessage],
    data () {
      return {
        priceList: {
          name: '',
          items: [],
        },
        menuItems: [],
        title: 'Новый прайс-лист',
        listRouteName: 'price-lists',
        valid: false,
        loading: false,
        saving: false,
        snackbar: false,
        showOnlyWithPriceItems: false,
      }
    },
    computed: {
      ...mapGetters({
        getMenuItems: 'menuItems/getMenuItemsList',
      }),
      getId () {
        return this.$route.params.id
      },
      filteredPriceListItemsForTable () {
        return this.showOnlyWithPriceItems
          ? this.menuItems.filter(item => isNumber(item.price))
          : this.menuItems
      },
    },
    created () {
      if (this.getId) {
        this.title = 'Редактирование прайс-листа'
      }

      this.initEntity()
    },
    methods: {
      async initEntity () {
        this.loading = true
        await this.fetchMenuItems()

        if (this.getId) {
          const entity = await this.fetchEntity()
          this.mapDataToItem(entity)
        } else {
          this.mapDataToItem()
        }

        this.loading = false
      },
      async preparePriceListItems (onlyActive) {
        this.loading = true
        try {
          await this.fetchMenuItems(onlyActive)
          this.mapDataToItem(this.priceList)
        } catch (err) {
          this.showErrorFromResponse(err)
        }
        this.loading = false
      },
      setShowOnlyWithPriceItems (value) {
        this.showOnlyWithPriceItems = !!value
      },
      preparePriceListItem (menuItem, price = null) {
        return {
          id: menuItem.id,
          externalId: menuItem.externalId,
          name: menuItem.name,
          categories: menuItem.categories ? menuItem.categories.slice() : [],
          collections: menuItem.collections ? menuItem.collections.slice() : [],
          basePrice: menuItem.price,
          image: menuItem.image,
          status: menuItem.status,
          price,
        }
      },
      async fetchMenuItems (onlyActive = true) {
        try {
          await this.$store.dispatch('menuItems/fetchMenuItemsList', {
            onlyActive,
            relations: ['categories', 'collections'],
          })
        } catch (err) {
          this.showErrorFromResponse(err)
        }
      },
      async fetchEntity () {
        try {
          return await PriceListDataService.getOne(this.getId)
        } catch (err) {
          this.showErrorFromResponse(err)
          return null
        }
      },
      async createNewEntity () {
        const validate = this.$refs.form.validate()

        if (!validate) {
          this.showValidationErrorMsg()
          return
        }

        this.saving = true
        try {
          await PriceListDataService.create(this.priceList)
          this.goToListing()
        } catch (err) {
          this.showErrorFromResponse(err)
        }

        this.saving = false
      },

      async editEntity ({ redirect = false }) {
        const validate = this.$refs.form.validate()
        if (!validate) {
          this.showValidationErrorMsg()
          return
        }

        this.saving = true
        try {
          const savedData = await PriceListDataService.update(this.getId, this.priceList)
          this.showSuccessMessage('Изменения сохранены')
          if (redirect) {
            this.goToListing()
          } else {
            this.mapDataToItem(savedData)
          }
        } catch (err) {
          this.showErrorFromResponse(err)
        }
        this.saving = false
      },
      handlePriceListItemsInput (items) {
        this.menuItems = this.menuItems.map(menuItem => {
          const priceListItem = items.find(item => menuItem.id === item.id)

          return { ...menuItem, price: priceListItem?.price }
        })
      },
      mapDataToItem (data) {
        this.priceList = {
          name: data?.name || '',
          items: data?.items?.slice() || [],
        }
        this.menuItems = this.getMenuItems.map((menuItem) => {
          const foundItem = data?.items.find((item) => item.id === menuItem.id)
          return this.preparePriceListItem(menuItem, foundItem?.price)
        })
      },
      showValidationErrorMsg () {
        this.snackbar = true
        document.getElementById('fcreate').scrollIntoView({
          behavior: 'smooth',
        })
        setTimeout(() => {
          this.snackbar = false
        }, 3000)
      },
      goToListing () {
        this.$router.push({ name: this.listRouteName })
      },
    },
  }
  </script>
